<template>
  <div
    id="sliceWrapper"
    ref="sliceWrapper"
    class="wrapper"
    :class="{'tourActive': tourCurrentStep >= 0 && (tourCurrentTarget === '#sliceWrapper')}"
  >
    <canvas
      id="sliceCanvas"
      ref="sliceCanvas"
    />
  </div>
</template>

<script>
import * as THREE from 'three'
import '@/vendor/STLLoader.js'
import { stackHelperFactory, trackballControlFactory, VolumeLoader } from 'ami.js'
import Level from '@/lib/level'
const loaderSTL = new THREE.STLLoader()
const fileContext = require.context(
  '!file-loader!@/../public/dicomFiles', false
)

export default {
  name: 'SliceView',
  props: {
    tourCurrentStep: {
      type: Number,
      default: undefined
    },
    tourCurrentTarget: {
      type: String,
      default: undefined
    },
    probePosition: {
      type: THREE.Vector3,
      default: undefined
    }
  },
  data: function () {
    return {
      cnvas: undefined,
      wrapper: undefined,

      cnvasSizeX: 0,
      cnvasSizeY: 0,

      VIEW_ANGLE: 45,
      NEAR: 0.1,
      FAR: 10000,

      camera: undefined,
      renderer: undefined,
      scene: undefined,
      model: {},
      volumeLoader: undefined,
      controls: undefined,
      StackHelper: undefined,
      stackHelper: undefined,
      planePosition: undefined,

      sliceMaskPath: '/sliceMask.stl',
      sondeModelPath: '/SondeModelS.stl',
      sliceLinePath: '/sliceLine.stl'
    }
  },
  mounted () {
    this.canvas = this.$refs.sliceCanvas
    this.wrapper = this.$refs.sliceWrapper

    this.canvas.width = 0
    this.canvas.height = 0
    this.canvasSizeX = this.wrapper.offsetWidth
    this.canvasSizeY = this.wrapper.offsetHeight
    this.setup()
    const ControlsTrackball = trackballControlFactory(THREE)
    this.controls = new ControlsTrackball(this.camera, this.canvas)
  },
  methods: {
    setup () {
      this.camera = new THREE.OrthographicCamera(this.canvasSizeX / -(this.canvasSizeX / 70), this.canvasSizeX / (this.canvasSizeX / 70), this.canvasSizeY / (this.canvasSizeX / 70), this.canvasSizeY / -(this.canvasSizeX / 70), 1, 1000)

      this.camera.position.set(150, 150, 150)

      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        canvas: this.canvas
      })
      this.scene = new THREE.Scene()
      this.scene.add(this.camera)
      this.renderer.setSize(this.canvasSizeX, this.canvasSizeY)
      this.addElements()
    },
    setSize () {
      this.renderer.setSize(0, 0)
      this.canvasSizeX = this.wrapper.offsetWidth
      this.canvasSizeY = this.wrapper.offsetHeight - 6
      this.renderer.setSize(this.canvasSizeX, this.canvasSizeY)
      this.camera.left = this.canvasSizeX / -5
      this.camera.right = this.canvasSizeX / 5
      this.camera.top = this.canvasSizeY / 5
      this.camera.bottom = this.canvasSizeY / -5
      this.camera.updateProjectionMatrix()
    },
    addElements () {
      loaderSTL.load(this.sondeModelPath, (object) => {
        // smooth shading von stack overflow: https://stackoverflow.com/questions/35136282/how-to-smooth-mesh-triangles-in-stl-loaded-buffergeometry
        /// ////////////////////////////////////////////////////////////

        var attrib = object.getAttribute('position')
        if (attrib === undefined) {
          throw new Error('a given BufferGeometry object must have a position attribute.')
        }
        var positions = attrib.array
        var vertices = []
        for (var i = 0, n = positions.length; i < n; i += 3) {
          var x = positions[i]
          var y = positions[i + 1]
          var z = positions[i + 2]
          vertices.push(new THREE.Vector3(x, y, z))
        }
        var faces = []
        for (var i = 0, n = vertices.length; i < n; i += 3) {
          faces.push(new THREE.Face3(i, i + 1, i + 2))
        }

        var geometry = new THREE.Geometry()
        geometry.vertices = vertices
        geometry.faces = faces
        geometry.computeFaceNormals()
        geometry.mergeVertices()
        geometry.computeVertexNormals()

        /// ////////////////////////////////////////////////////////////
        var material = new THREE.MeshLambertMaterial({ color: 0xfdfdfd })
        var mesh = new THREE.Mesh(geometry, material)

        this.model.sonde = mesh
        this.scene.add(this.model.sonde)
      })

      loaderSTL.load(this.sliceLinePath, geometry => {
        var material = new THREE.MeshLambertMaterial({ color: 0x34ebeb })
        var mesh = new THREE.Mesh(geometry, material)
        this.model.sliceLine = mesh
        this.scene.add(this.model.sliceLine)
      })

      loaderSTL.load(this.sliceMaskPath, geometry => {
        var material = new THREE.MeshLambertMaterial({ color: 0x121339 })
        var mesh = new THREE.Mesh(geometry, material)
        mesh.rotation.y = Math.PI
        this.model.sliceMask = mesh
        this.scene.add(this.model.sliceMask)
      })

      this.scene.add(new THREE.AmbientLight(0xffffff))
      this.render()
    },
    async loadLevel (gameToLoad, roundToLoad) {
      const file = fileContext('./' + Level[gameToLoad].rounds[roundToLoad].stackPath)
      this.volumeLoader = new VolumeLoader(this.renderer.domElement)
      this.volumeLoader
        .load(file)
        .then(() => {
          const series = this.volumeLoader.data[0].mergeSeries(this.volumeLoader.data)[0]
          const stack = series.stack[0]

          if (this.stackHelper) {
            this.scene.remove(this.stackHelper)
            this.StackHelper = stackHelperFactory(THREE)
            this.stackHelper = new this.StackHelper(stack)
            this.scene.add(this.stackHelper)
          } else {
            this.StackHelper = stackHelperFactory(THREE)
            this.stackHelper = new this.StackHelper(stack)
            this.scene.add(this.stackHelper)
          }

          this.$store.commit('setStackCenter', this.stackHelper.stack.worldCenter())

          this.planePosition = new THREE.Vector3(this.$store.state.stackCenter.x, this.$store.state.stackCenter.y, this.$store.state.stackCenter.z)

          this.camera.lookAt(this.planePosition)

          this.volumeLoader.free()
          this.volumeLoader = null
        })
        .then(() => {
          this.$store.commit('setLoading', false)
          this.stackHelper.bbox.visible = false
          this.stackHelper.border.visible = false
        })
        .catch(function (error) {
          window.console.log('oops... something went wrong...')
          window.console.log(error)
        })
    },
    render () {
      // if (this.controls) {
      //   this.controls.update()
      // }
      if (this.stackHelper) {
        // in welche richtung guckt die Sonde
        this.planePositionDirection = new THREE.Vector3(
          Math.sin(Math.PI * (this.$store.state.rotateXZ - 0.5)) * Math.sin(Math.PI * this.$store.state.rotateY),
          Math.cos(Math.PI * (this.$store.state.rotateXZ - 0.5)),
          Math.sin(Math.PI * (this.$store.state.rotateXZ - 0.5)) * Math.cos(Math.PI * this.$store.state.rotateY)
        ).normalize()

        if (this.$store.state.probePosition) {
        // #plane
        // -----------------------------------------------------------------------------------------------------------------------------------------------------------
          this.planePosition = new THREE.Vector3(
            this.$store.state.probePosition.x + (this.planePositionDirection.x * this.$store.state.stackCenter.x),
            this.$store.state.probePosition.y + (this.planePositionDirection.y * this.$store.state.stackCenter.y),
            this.$store.state.probePosition.z + (this.planePositionDirection.z * this.$store.state.stackCenter.z)
          )
          this.stackHelper.slice.planePosition = this.planePosition
          this.$store.commit('setPlaneDirection', new THREE.Vector3(
            Math.sin(Math.PI * this.$store.state.rotateXZ) * Math.sin(Math.PI * this.$store.state.rotateY),
            Math.cos(Math.PI * this.$store.state.rotateXZ),
            Math.sin(Math.PI * this.$store.state.rotateXZ) * Math.cos(Math.PI * this.$store.state.rotateY)
          ).normalize())
          this.stackHelper.slice.planeDirection = this.$store.state.planeDirection
        }
        if (this.$store.state.probePosition) {
        // #sonde
        // -----------------------------------------------------------------------------------------------------------------------------------------------------------
          this.model.sonde.scale.set(0.5, 0.5, 0.5)
          this.model.sonde.position.set(50, 50, 50)
          this.model.sonde.position.set(
            this.$store.state.probePosition.x + (this.$store.state.planeDirection.x * 10),
            this.$store.state.probePosition.y + (this.$store.state.planeDirection.y * 10),
            this.$store.state.probePosition.z + (this.$store.state.planeDirection.z * 10)
          )
          this.model.sonde.lookAt(
            this.model.sonde.position.x + this.$store.state.planeDirection.x,
            this.model.sonde.position.y + this.$store.state.planeDirection.y,
            this.model.sonde.position.z + this.$store.state.planeDirection.z
          )
          this.model.sliceLine.position.set(
            this.$store.state.probePosition.x + (this.$store.state.planeDirection.x * 10),
            this.$store.state.probePosition.y + (this.$store.state.planeDirection.y * 10),
            this.$store.state.probePosition.z + (this.$store.state.planeDirection.z * 10)
          )
          this.model.sliceLine.scale.set(35, 35, 35)
          this.model.sliceLine.lookAt(
            this.model.sonde.position.x + this.$store.state.planeDirection.x,
            this.model.sonde.position.y + this.$store.state.planeDirection.y,
            this.model.sonde.position.z + this.$store.state.planeDirection.z
          )
        }
        if (this.model.sliceLine && this.$store.state.planeDirection) {
          this.model.sliceLine.scale.set(35, 35, 35)
          this.model.sliceLine.lookAt(
            this.model.sonde.position.x + this.$store.state.planeDirection.x,
            this.model.sonde.position.y + this.$store.state.planeDirection.y,
            this.model.sonde.position.z + this.$store.state.planeDirection.z
          )
        }
        if (this.model.sliceMask && this.$store.state.planeDirection) {
        // #sliceMask
        // -----------------------------------------------------------------------------------------------------------------------------------------------------------
          this.model.sliceMask.scale.set(35, 35, 35)
          this.model.sliceMask.position.set(
            this.planePosition.x + (this.$store.state.planeDirection.x * 10),
            this.planePosition.y + (this.$store.state.planeDirection.y * 10),
            this.planePosition.z + (this.$store.state.planeDirection.z * 10)
          )
          this.model.sliceMask.lookAt(
            this.planePosition.x + this.$store.state.planeDirection.x,
            this.planePosition.y + this.$store.state.planeDirection.y,
            this.planePosition.z + this.$store.state.planeDirection.z
          )
        }
        if (this.$store.state.planeDirection) {
          this.camera.position.set(
            this.planePosition.x + (this.$store.state.planeDirection.x * 100),
            this.planePosition.y + (this.$store.state.planeDirection.y * 100),
            this.planePosition.z + (this.$store.state.planeDirection.z * 100)
          )
        }
        this.camera.lookAt(this.planePosition.x, this.planePosition.y, this.planePosition.z)
      }
      this.renderer.render(this.scene, this.camera)
    }
  }
}
</script>

<style lang="scss" scoped>
.wrapper{
  grid-area: slice;
  canvas{
    width: 0;
  }
}
</style>
